import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Shield,
  HeartPulse,
  BarChart3,
  ArrowRight,
  Layers,
  FileText,
  Cpu,
  DollarSign,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Link2,
  KeyRound,
} from 'lucide-react'
import { api, type KongHealthResponse, type AdminContext } from '@services/api'
import { toast } from 'react-toastify'

// ─── Types ────────────────────────────────────────────────────────────────

interface IntegrationStats {
  documents: { context_id: number; name: string; display_name: string; document_count: number }[]
  users: { context_id: number; name: string; user_count: number }[]
  uniqueUsers: number
  llm: {
    allTime: { total_calls: number; total_input_tokens: number; total_output_tokens: number; total_tokens: number }
    last30Days: { calls: number; input_tokens: number; output_tokens: number; total_tokens: number }
    byContext: {
      context_id: number
      name: string
      display_name: string
      calls: number
      input_tokens: number
      output_tokens: number
    }[]
    estimatedCostUsd: number
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function fmt(n: number, decimals = 1): string {
  return n.toFixed(decimals)
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return String(n)
}

// ─── SuperAdminDashboard ──────────────────────────────────────────────────

const SuperAdminDashboard: React.FC = () => {
  const navigate = useNavigate()
  const [contexts, setContexts] = useState<AdminContext[]>([])
  const [kongHealth, setKongHealth] = useState<KongHealthResponse | null>(null)
  const [integrationStats, setIntegrationStats] = useState<IntegrationStats | null>(null)
  const [loading, setLoading] = useState(true)

  const loadAll = async () => {
    setLoading(true)
    const results = await Promise.allSettled([
      api.adminGetContexts(),
      api.adminGetKongHealth(),
      api.getIntegrationStats(),
    ])
    if (results[0].status === 'fulfilled') setContexts(results[0].value)
    else toast.error('Failed to load contexts')
    if (results[1].status === 'fulfilled') setKongHealth(results[1].value)
    else toast.error('Failed to load Kong health')
    if (results[2].status === 'fulfilled') setIntegrationStats(results[2].value)
    else toast.error('Failed to load integration stats')
    setLoading(false)
  }

  useEffect(() => {
    loadAll()
  }, [])

  if (loading && !kongHealth) {
    return (
      <div className="flex h-64 items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    )
  }

  const nonAdminContexts = contexts.filter((c) => c.name !== 'superadmin')
  const activeContexts = nonAdminContexts.filter((c) => c.is_active)
  const entraConfigured = nonAdminContexts.filter((c) => c.entra_group_id)
  const totalDocs = integrationStats?.documents.reduce((s, d) => s + d.document_count, 0) ?? 0
  const dbReachable = kongHealth?.status.database === 'reachable'

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Admin Dashboard</h1>
        <Link
          to="/admin/api-access-requests"
          className="inline-flex items-center space-x-2 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-100 dark:border-indigo-800/50 dark:bg-indigo-900/30 dark:text-indigo-300 dark:hover:bg-indigo-900/40"
        >
          <KeyRound className="h-4 w-4" />
          <span>API Access Requests</span>
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
      {/* ─── Contexts Section ─────────────────────────────────────────── */}
      <section>
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-100 dark:bg-indigo-900/40">
              <Shield className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Contexts</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Business-unit contexts and memberships</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/admin')}
            className="inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-100 dark:border-indigo-800/50 dark:bg-indigo-900/30 dark:text-indigo-300 dark:hover:bg-indigo-900/40"
          >
            <span>Details</span>
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="card flex items-start gap-4 p-4">
            <div className="rounded-lg bg-indigo-100 p-2 dark:bg-indigo-900/40">
              <Layers className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Active Contexts</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {activeContexts.length}
                <span className="ml-1 text-sm font-normal text-gray-400 dark:text-gray-500">
                  / {nonAdminContexts.length}
                </span>
              </p>
            </div>
          </div>

          <div className="card flex items-start gap-4 p-4">
            <div className="rounded-lg bg-emerald-100 p-2 dark:bg-emerald-900/40">
              <Link2 className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Entra Sync Configured</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {entraConfigured.length}
                <span className="ml-1 text-sm font-normal text-gray-400 dark:text-gray-500">
                  / {nonAdminContexts.length}
                </span>
              </p>
            </div>
          </div>
          <div className="card flex items-start gap-4 p-4">
            <div className="rounded-lg bg-amber-100 p-2 dark:bg-amber-900/40">
              {entraConfigured.length === nonAdminContexts.length ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              ) : (
                <XCircle className="h-5 w-5 text-amber-600" />
              )}
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Entra Status</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {entraConfigured.length === nonAdminContexts.length ? 'All linked' : 'Incomplete'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Kong Health & Routing Section ────────────────────────────── */}
      <section>
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 dark:bg-emerald-900/40">
              <HeartPulse className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Kong Health</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Gateway status and routing metrics
                {kongHealth && (
                  <span className="ml-2 text-gray-400 dark:text-gray-500">
                    · {new Date(kongHealth.fetchedAt).toLocaleTimeString()}
                  </span>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/admin/kong-health')}
            className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800/50 dark:bg-emerald-900/30 dark:text-emerald-300 dark:hover:bg-emerald-900/40"
          >
            <span>Details</span>
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="card flex items-start gap-4 p-4">
            <div className="rounded-lg bg-emerald-100 p-2 dark:bg-emerald-900/40">
              {dbReachable ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              ) : (
                <XCircle className="h-5 w-5 text-red-500" />
              )}
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Database</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{dbReachable ? 'Reachable' : 'Down'}</p>
            </div>
          </div>
          <div className="card flex items-start gap-4 p-4">
            <div className="rounded-lg bg-indigo-100 p-2 dark:bg-indigo-900/40">
              <BarChart3 className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Total Requests</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {formatNumber(kongHealth?.totals.requests ?? 0)}
              </p>
            </div>
          </div>
          <div className="card flex items-start gap-4 p-4">
            <div className="rounded-lg bg-violet-100 p-2 dark:bg-violet-900/40">
              <BarChart3 className="h-5 w-5 text-violet-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">p95 Latency</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {kongHealth ? `${fmt(kongHealth.totals.p95Ms)} ms` : '—'}
              </p>
            </div>
          </div>
          <div className="card flex items-start gap-4 p-4">
            <div className="rounded-lg bg-sky-100 p-2 dark:bg-sky-900/40">
              <BarChart3 className="h-5 w-5 text-sky-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Routes Active</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{kongHealth?.routes.length ?? 0}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Integration Admin Section ────────────────────────────────── */}
      <section>
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-900/40">
              <BarChart3 className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Integration Admin</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Document digestion, LLM consumption, and cross-context stats
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/admin/integration')}
            className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-100 dark:border-blue-800/50 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/40"
          >
            <span>Details</span>
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="card flex items-start gap-4 p-4">
            <div className="rounded-lg bg-blue-100 p-2 dark:bg-blue-900/40">
              <FileText className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Documents Digested</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{formatNumber(totalDocs)}</p>
            </div>
          </div>
          <div className="card flex items-start gap-4 p-4">
            <div className="rounded-lg bg-purple-100 p-2 dark:bg-purple-900/40">
              <Cpu className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">LLM Calls (All Time)</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {formatNumber(integrationStats?.llm.allTime.total_calls ?? 0)}
              </p>
            </div>
          </div>
          <div className="card flex items-start gap-4 p-4">
            <div className="rounded-lg bg-green-100 p-2 dark:bg-green-900/40">
              <BarChart3 className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Tokens Used (All Time)</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {formatNumber(integrationStats?.llm.allTime.total_tokens ?? 0)}
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/admin/finops')}
            className="card flex items-start gap-4 p-4 text-left transition hover:border-amber-300 hover:shadow"
          >
            <div className="rounded-lg bg-amber-100 p-2 dark:bg-amber-900/40">
              <DollarSign className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Estimated AI Cost</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                ${(integrationStats?.llm.estimatedCostUsd ?? 0).toFixed(2)}
              </p>
              <p className="mt-0.5 inline-flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-300">
                FinOps dashboard <ArrowRight className="h-3 w-3" />
              </p>
            </div>
          </button>
        </div>

        {integrationStats && (
          <div className="card overflow-hidden p-0">
            <table className="w-full text-sm">
              <thead className="border-b bg-gray-50 dark:bg-white/5">
                <tr className="text-left text-gray-500 dark:text-gray-400">
                  <th className="px-5 py-3 font-medium">Context</th>
                  <th className="px-5 py-3 text-right font-medium">Documents</th>
                  <th className="px-5 py-3 text-right font-medium">LLM Calls</th>
                  <th className="px-5 py-3 text-right font-medium">Tokens</th>
                </tr>
              </thead>
              <tbody>
                {integrationStats.documents.map((d) => {
                  const llm = integrationStats.llm.byContext.find((l) => l.context_id === d.context_id)
                  return (
                    <tr key={d.context_id} className="border-b last:border-0">
                      <td className="px-5 py-3 font-medium text-gray-900 dark:text-gray-100">{d.display_name}</td>
                      <td className="px-5 py-3 text-right text-gray-700 dark:text-gray-300">{d.document_count}</td>
                      <td className="px-5 py-3 text-right text-gray-700 dark:text-gray-300">
                        {formatNumber(llm?.calls ?? 0)}
                      </td>
                      <td className="px-5 py-3 text-right text-gray-700 dark:text-gray-300">
                        {formatNumber((llm?.input_tokens ?? 0) + (llm?.output_tokens ?? 0))}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}

export default SuperAdminDashboard
